import {
  GetPaymentMethodsDocument,
  useAddPaymentMethodMutation,
  WithdrawalPaymentMethod,
} from '@/affiliate-dashboard/graphql';
import { Wizard } from '@/components/base/wizard';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { CryptoStep } from './crypto';
import { Crypto } from './crypto-details-form';
import { RiseDetailsFormForWizard } from './rise-details-form';
import { SelectPayoutMethod } from './select-method';

export function getCryptoAddressValidation({
  address,
  currency,
}: {
  address: string;
  currency: Crypto;
}): boolean {
  if (currency === Crypto.USDC) {
    const tetherRegex = /^0x[a-fA-F0-9]{40}$/;
    return tetherRegex.test(address);
  }
  return false;
}

function risePayoutMethodSchema(t: (key: string) => string) {
  return z.object({
    targetAddress: z
      .string()
      .min(1, { message: t('errorMessages.emailRequired') })
      .email(),
    displayName: z.string().min(1, { message: t('errorMessages.displayName') }),
    crypto: z.nativeEnum(Crypto).optional(),
  });
}

function cryptoPayoutMethodSchema(t: (key: string) => string) {
  return z
    .object({
      crypto: z.nativeEnum(Crypto),
      targetAddress: z
        .string()
        .min(1, { message: t('errorMessages.cryptoAddress') }),
      targetAddressConfirm: z
        .string()
        .min(1, { message: t('errorMessages.cryptoAddressConfirmation') }),
      displayName: z
        .string()
        .min(1, { message: t('errorMessages.displayName') }),
    })
    .refine(
      (data) => {
        return getCryptoAddressValidation({
          address: data.targetAddress,
          currency: data.crypto,
        });
      },
      {
        message: t('errorMessages.cryptoAddressInvalid'),
        path: ['targetAddress'],
      },
    )
    .refine(
      (data) => {
        return data.targetAddress === data.targetAddressConfirm;
      },
      {
        message: t('errorMessages.cryptoAddressMatch'),
        path: ['targetAddressConfirm'],
      },
    );
}

export function AddPayoutMethod({
  onClose,
}: {
  onClose: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [selectedMethod, setSelectedMethod] =
    useState<WithdrawalPaymentMethod | null>();
  const [addPaymentMethod, { loading }] = useAddPaymentMethodMutation();
  const schema =
    selectedMethod === WithdrawalPaymentMethod.DirectCrypto
      ? cryptoPayoutMethodSchema(t)
      : risePayoutMethodSchema(t);
  const { enqueueSnackbar } = useSnackbar();

  type PayoutMethodSchema = z.infer<typeof schema>;
  const methods = useForm<PayoutMethodSchema>({
    resolver: zodResolver(schema),
    mode: 'onChange',
  });

  const { handleSubmit } = methods;

  function handleFinish(): void {
    handleSubmit(async (values) => {
      const metadata =
        selectedMethod === WithdrawalPaymentMethod.DirectCrypto
          ? (values.crypto as string)
          : null;
      try {
        await addPaymentMethod({
          variables: {
            paymentMethodInput: {
              paymentMethod: selectedMethod,
              targetAddress: values.targetAddress,
              displayName: values.displayName,
              paymentMethodMetadata: JSON.stringify({ network: metadata }),
            },
          },
          refetchQueries: [{ query: GetPaymentMethodsDocument }],
        });
        enqueueSnackbar({
          message: 'Payment method added',
          variant: 'success',
        });
        onClose();
      } catch (e) {
        enqueueSnackbar({
          message: 'error',
          variant: 'error',
        });
      }
    })();
  }
  const steps = [
    {
      id: 1,
      label: 'Select Payout Method',
      component: <SelectPayoutMethod onChange={setSelectedMethod} />,
    },
    {
      id: 2,
      label: 'Selected Method',
      component:
        selectedMethod === WithdrawalPaymentMethod.Rise ? (
          <RiseDetailsFormForWizard loading={loading} />
        ) : (
          <CryptoStep loading={loading} onFinish={handleFinish} />
        ),
    },
  ];

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
        }}
      >
        <Wizard onFinish={handleFinish} loading={loading} steps={steps} />
      </form>
    </FormProvider>
  );
}
